import {
  getOnlineStaticDatabase,
  getStaticDatabase,
  type StaticDatabaseConnection,
  type StaticDatabaseHandle,
} from "@-/dev.improve.haniverse.database.static";
import {
  getLevelPackConnection,
  getLevelPackIdentity,
  getPackDataIdentity,
  usePackDataIdentity,
} from "@-/dev.improve.haniverse.lib.database.shards";
import { resolveHanWorkerUrl } from "@-/dev.improve.haniverse.lib.worker";
import { useCallback } from "react";
import { onlineManager } from "@-/com.npmjs.tanstack__react-query";

export type GetStaticDatabase = () => Promise<StaticDatabaseHandle>;
export type StaticDataSource = "packs" | "online";
export type GetStaticDataSource = () => Promise<StaticDataSource>;
export type GetStaticDatabaseConnection = () => Promise<StaticDatabaseConnection | null>;
interface StaticDatabaseLoadResult {
  connection: StaticDatabaseConnection | null;
  database: StaticDatabaseHandle;
  source: StaticDataSource;
}
const staticDatabaseCache = new Map<string, StaticDatabaseLoadResult>();
export function setCachedStaticDatabase(
  result: Omit<StaticDatabaseLoadResult, "source"> & { source?: StaticDataSource },
): void {
  staticDatabaseCache.set(getPackDataIdentity(), { source: "packs", ...result });
}
async function loadStaticDatabase(identity: string): Promise<StaticDatabaseLoadResult> {
  const seeded = staticDatabaseCache.get(identity);
  if (seeded !== undefined) {
    return seeded;
  }
  if (onlineManager.isOnline()) {
    return {
      connection: null,
      database: getOnlineStaticDatabase(resolveHanWorkerUrl()),
      source: "online",
    };
  }
  const connection = await getLevelPackConnection(getLevelPackIdentity());
  if (connection === null) {
    throw new Error(
      "No level packs are downloaded. Connect to download a pack in Settings → Offline Packs.",
    );
  }
  return { connection, database: getStaticDatabase(connection), source: "packs" };
}
export function clearCachedStaticDatabase(): void {
  staticDatabaseCache.clear();
}
export function useStaticDatabaseGeneration(): string {
  return usePackDataIdentity();
}
export function useGetStaticDatabase(): GetStaticDatabase {
  const identity = useStaticDatabaseGeneration();
  return useCallback(async () => {
    const { database } = await loadStaticDatabase(identity);
    return database;
  }, [identity]);
}
export function useGetStaticDataSource(): GetStaticDataSource {
  const identity = useStaticDatabaseGeneration();
  return useCallback(async () => {
    const { source } = await loadStaticDatabase(identity);
    return source;
  }, [identity]);
}
export function useGetStaticDatabaseConnection(): GetStaticDatabaseConnection {
  const identity = useStaticDatabaseGeneration();
  return useCallback(async () => {
    const { connection } = await loadStaticDatabase(identity);
    return connection;
  }, [identity]);
}
